import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  AlertTriangle,
  Calendar,
  Eye,
  FileText,
  Mail,
  MapPin,
  Phone,
  Plus,
  Search,
  Trash2,
  User,
} from "lucide-react";
import { isoDateToMDY } from "../../../utils/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { YesNoDialog } from "@/components/ui/modules/yes-no-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/toast";
import { TabHeader } from "../shared/components/TabHeader";
import { TabSpinner } from "../shared/components/TabSpinner";
import { TabTable } from "../shared/components/TabTable";
import { TabToolbar } from "../shared/components/TabToolbar";
import {
  CREATE_PARTICIPANT_OUTREACH,
  DELETE_PARTICIPANT_OUTREACH,
  GET_PARTICIPANT_OUTREACH,
} from "./queries";

interface OutreachAttempt {
  id: number;
  contactMethod: number | null;
  contactDate: string | null;
  results: number | null;
  qaStaff: string | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  email: string | null;
  phoneNumber: string | null;
  nameOfContact: string | null;
  notes: string | null;
}

interface OutreachQueryData {
  getParticipantByODRNumber: {
    id: number;
    outreach: OutreachAttempt[];
  };
}

interface OutreachTabProps {
  odrnumber: number;
  participantId?: number | null;
}

interface OutreachFormState {
  contactMethod: string;
  date: string;
  results: string;
  qaStaff: string;
  address: string;
  city: string;
  zip: string;
  email: string;
  phoneNumber: string;
  nameOfContact: string;
  notes: string;
}

const CONTACT_METHOD_OPTIONS = [
  { value: 0, label: "Email" },
  { value: 1, label: "Phone" },
  { value: 2, label: "In Person" },
];

const RESULT_OPTIONS = [
  { value: 0, label: "Rejected to Enroll" },
  { value: 1, label: "Agreed to Enroll" },
  { value: 2, label: "Waiting Response (First Attempt)" },
  { value: 3, label: "Waiting Response (Follow Up Attempt)" },
];

const QA_STAFF_OPTIONS = [
  "Antony Schreurs",
  "Diana Ramirez",
  "William Prewett",
];

const INITIAL_FORM_STATE: OutreachFormState = {
  contactMethod: "",
  date: "",
  results: "",
  qaStaff: "",
  address: "",
  city: "",
  zip: "",
  email: "",
  phoneNumber: "",
  nameOfContact: "",
  notes: "",
};

const getContactMethodLabel = (value: number | null | undefined) => {
  const option = CONTACT_METHOD_OPTIONS.find((item) => item.value === Number(value));
  if (!option) return "—";
  return option.label;
};

const getResultLabel = (value: number | null | undefined) => {
  const option = RESULT_OPTIONS.find((item) => item.value === Number(value));
  if (!option) return "—";
  return option.label;
};

export const OutreachTab: React.FC<OutreachTabProps> = ({ odrnumber, participantId }) => {
  const [search, setSearch] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDeleteOutreachId, setPendingDeleteOutreachId] = useState<number | null>(null);
  const [selectedOutreach, setSelectedOutreach] = useState<OutreachAttempt | null>(null);
  const [formState, setFormState] = useState<OutreachFormState>(INITIAL_FORM_STATE);

  const { loading, error, data, refetch } = useQuery<OutreachQueryData>(GET_PARTICIPANT_OUTREACH, {
    variables: { odrnumber },
    skip: !odrnumber,
  });

  const [createParticipantOutreach] = useMutation(CREATE_PARTICIPANT_OUTREACH);
  const [deleteParticipantOutreach] = useMutation(DELETE_PARTICIPANT_OUTREACH);

  const outreachRows = data?.getParticipantByODRNumber?.outreach || [];

  const sortedOutreachRows = useMemo(() => {
    return [...outreachRows].sort((a, b) => {
      const aTime = a.contactDate ? new Date(a.contactDate).getTime() : 0;
      const bTime = b.contactDate ? new Date(b.contactDate).getTime() : 0;
      return bTime - aTime;
    });
  }, [outreachRows]);

  const filteredOutreach = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedOutreachRows;

    return sortedOutreachRows.filter((attempt) => {
      const contactDate = isoDateToMDY(attempt.contactDate || "").toLowerCase();
      const contactMethod = getContactMethodLabel(attempt.contactMethod).toLowerCase();
      const result = getResultLabel(attempt.results).toLowerCase();
      const qaStaff = (attempt.qaStaff || "").toLowerCase();
      const nameOfContact = (attempt.nameOfContact || "").toLowerCase();
      const email = (attempt.email || "").toLowerCase();
      return (
        contactDate.includes(term) ||
        contactMethod.includes(term) ||
        result.includes(term) ||
        qaStaff.includes(term) ||
        nameOfContact.includes(term) ||
        email.includes(term)
      );
    });
  }, [search, sortedOutreachRows]);

  const updateForm = (field: keyof OutreachFormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormState(INITIAL_FORM_STATE);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsCreateOpen(open);
    if (open) return;
    resetForm();
  };

  const handleCreateOutreach = async () => {
    if (!formState.date) return toast.error("Please enter a date.");
    if (!formState.contactMethod) return toast.error("Please select a contact method.");
    if (!formState.results) return toast.error("Please select a result.");
    if (!formState.qaStaff) return toast.error("Please select QA staff.");

    const candidateParticipantId = participantId ?? data?.getParticipantByODRNumber?.id;
    const participant = Number(candidateParticipantId);
    if (!Number.isInteger(participant)) {
      toast.error("Participant id is missing. Please refresh and try again.");
      return;
    }

    const input = {
      contactMethod: formState.contactMethod || undefined,
      date: formState.date || undefined,
      results: formState.results || undefined,
      qaStaff: formState.qaStaff || undefined,
      address: formState.address || undefined,
      city: formState.city || undefined,
      zip: formState.zip || undefined,
      email: formState.email || undefined,
      phoneNumber: formState.phoneNumber || undefined,
      nameOfContact: formState.nameOfContact || undefined,
      notes: formState.notes || undefined,
    };

    setIsSaving(true);

    try {
      await createParticipantOutreach({
        variables: {
          odrnumber,
          participant,
          input,
        },
      });
      toast.success("Client outreach was created.");
      await refetch();
      handleDialogOpenChange(false);
    } catch (mutationError: any) {
      toast.error(mutationError?.message || "Failed to create outreach.");
    }

    setIsSaving(false);
  };

  const handleDeleteOutreach = async () => {
    if (pendingDeleteOutreachId === null) return;

    const outreachId = pendingDeleteOutreachId;
    setIsDeleting(true);

    try {
      await deleteParticipantOutreach({
        variables: { outreachid: outreachId },
      });
      toast.success("Client outreach was deleted.");
      setPendingDeleteOutreachId(null);
      setSelectedOutreach(null);
      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError?.message || "Failed to delete outreach.");
    }

    setIsDeleting(false);
  };

  if (loading) return <TabSpinner label="Loading client outreach..." />;

  if (error) {
    return (
      <div className="p-12">
        <EmptyState
          icon={AlertTriangle}
          title="Error loading client outreach"
          description={error.message}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 h-full flex flex-col">
      <TabHeader
        icon={Phone}
        title="Client Outreach"
        subtitle="Outreach attempts linked to this participant"
        action={(
          <Button onClick={() => setIsCreateOpen(true)} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Add Client Outreach
          </Button>
        )}
      />

      <TabToolbar
        left={(
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search outreach attempts..."
              className="h-8 w-64 pl-8 text-xs bg-gray-50/50 border-gray-200 focus-visible:ring-1"
            />
          </div>
        )}
        right={<span className="text-xs text-gray-500">{filteredOutreach.length} attempts</span>}
      />

      <div className="flex-1 min-h-0 flex flex-col">
        <Card className="flex-1 overflow-hidden rounded-none border-0 shadow-none bg-white">
          <CardContent className="p-0 h-full flex flex-col">
            <div className="border-t border-b border-gray-200 flex-1 overflow-auto">
              <TabTable<OutreachAttempt>
                data={filteredOutreach}
                emptyMessage={search ? "No matching outreach attempts found." : "No outreach attempts recorded for this participant."}
                columns={[
                  {
                    title: "Date",
                    className: "w-[140px]",
                    render: (attempt) => (
                      <span className="text-xs font-medium text-gray-900">
                        {isoDateToMDY(attempt.contactDate || "") || "—"}
                      </span>
                    ),
                  },
                  {
                    title: "Contact Method",
                    className: "w-[170px]",
                    render: (attempt) => getContactMethodLabel(attempt.contactMethod),
                  },
                  {
                    title: "Result",
                    className: "min-w-[240px]",
                    render: (attempt) => getResultLabel(attempt.results),
                  },
                  {
                    title: "QA Staff",
                    className: "w-[180px]",
                    render: (attempt) => attempt.qaStaff || "—",
                  },
                  {
                    title: "Actions",
                    className: "w-[80px] text-right",
                    render: (attempt) => (
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                          title="View details"
                          onClick={() => setSelectedOutreach(attempt)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={isCreateOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Client Outreach
            </DialogTitle>
            <DialogDescription>
              Create a new outreach attempt for this participant.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="outreach-date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Date
                </Label>
                <Input
                  id="outreach-date"
                  type="date"
                  value={formState.date}
                  onChange={(e) => updateForm("date", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Contact Method
                </Label>
                <Select value={formState.contactMethod} onValueChange={(value) => updateForm("contactMethod", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact method" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_METHOD_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Result
                </Label>
                <Select value={formState.results} onValueChange={(value) => updateForm("results", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select result" />
                  </SelectTrigger>
                  <SelectContent>
                    {RESULT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4" />
                QA Staff
              </Label>
              <Select value={formState.qaStaff} onValueChange={(value) => updateForm("qaStaff", value)}>
                <SelectTrigger className="max-w-md">
                  <SelectValue placeholder="Select QA staff" />
                </SelectTrigger>
                <SelectContent>
                  {QA_STAFF_OPTIONS.map((staffName) => (
                    <SelectItem key={staffName} value={staffName}>
                      {staffName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="outreach-address" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Address
                </Label>
                <Input
                  id="outreach-address"
                  value={formState.address}
                  onChange={(e) => updateForm("address", e.target.value)}
                  placeholder="Address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="outreach-city">City</Label>
                <Input
                  id="outreach-city"
                  value={formState.city}
                  onChange={(e) => updateForm("city", e.target.value)}
                  placeholder="City"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="outreach-zip">ZIP</Label>
                <Input
                  id="outreach-zip"
                  value={formState.zip}
                  onChange={(e) => updateForm("zip", e.target.value)}
                  placeholder="ZIP"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="outreach-email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="outreach-email"
                  type="email"
                  value={formState.email}
                  onChange={(e) => updateForm("email", e.target.value)}
                  placeholder="Email address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="outreach-phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number
                </Label>
                <Input
                  id="outreach-phone"
                  value={formState.phoneNumber}
                  onChange={(e) => updateForm("phoneNumber", e.target.value)}
                  placeholder="Phone number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="outreach-contact" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Name of Contact
                </Label>
                <Input
                  id="outreach-contact"
                  value={formState.nameOfContact}
                  onChange={(e) => updateForm("nameOfContact", e.target.value)}
                  placeholder="Contact name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="outreach-notes" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Notes
              </Label>
              <Textarea
                id="outreach-notes"
                value={formState.notes}
                onChange={(e) => updateForm("notes", e.target.value)}
                placeholder="Additional notes"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleDialogOpenChange(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleCreateOutreach} disabled={isSaving}>
              {isSaving ? "Saving..." : "Create Outreach Attempt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedOutreach)}
        onOpenChange={(open: boolean) => {
          if (open) return;
          setSelectedOutreach(null);
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Client Outreach Details
            </DialogTitle>
          </DialogHeader>

          {selectedOutreach && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 rounded-md border border-gray-200 p-4 md:grid-cols-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Date</div>
                  <div className="text-sm font-medium text-gray-900">{isoDateToMDY(selectedOutreach.contactDate || "") || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Contact Method</div>
                  <div className="text-sm font-medium text-gray-900">{getContactMethodLabel(selectedOutreach.contactMethod)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Result</div>
                  <div className="text-sm font-medium text-gray-900">{getResultLabel(selectedOutreach.results)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">QA Staff</div>
                  <div className="text-sm font-medium text-gray-900">{selectedOutreach.qaStaff || "—"}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 rounded-md border border-gray-200 p-4 md:grid-cols-2">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Address</div>
                  <div className="text-sm text-gray-900">{selectedOutreach.address || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">City</div>
                  <div className="text-sm text-gray-900">{selectedOutreach.city || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">ZIP</div>
                  <div className="text-sm text-gray-900">{selectedOutreach.zip || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Email</div>
                  <div className="text-sm text-gray-900">{selectedOutreach.email || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Phone Number</div>
                  <div className="text-sm text-gray-900">{selectedOutreach.phoneNumber || "—"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Name of Contact</div>
                  <div className="text-sm text-gray-900">{selectedOutreach.nameOfContact || "—"}</div>
                </div>
              </div>

              <div className="rounded-md border border-gray-200 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">Notes</div>
                <div className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedOutreach.notes || "—"}</div>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  onClick={() => setPendingDeleteOutreachId(selectedOutreach.id)}
                  disabled={isDeleting}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {isDeleting ? "Deleting..." : "Delete Outreach"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <YesNoDialog
        open={pendingDeleteOutreachId !== null}
        onOpenChange={(open: boolean) => {
          if (open) return;
          setPendingDeleteOutreachId(null);
        }}
        title="Delete Client Outreach"
        description="Delete this outreach attempt?"
        onConfirm={handleDeleteOutreach}
        confirmDisabled={isDeleting || pendingDeleteOutreachId === null}
      />
    </div>
  );
};
